import {
  bornesDeChangeDebit,
  changeDansLaBandeDebit,
  couvertDansLaDevise,
  ecartJours,
  formaterJour,
  formaterJourLong,
  formaterMontant,
  JOURS_MAX_SANS_CONCORDANCE,
  MOIS,
  MOTIFS_COMPENSATION_DITS,
} from '@admin-pmvb/core'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { LogoConnu } from '../composants/Logo'
import { api, ErreurApi, lire, requetes } from '../api'
import { texteOperationCabinet } from '../composants/bilan-justificatifs'
import { Icone } from '../composants/Icone'
import { Vide } from '../composants/Section'

// Écran de décision du rapprochement bancaire : une opération du relevé, et une seule question en
// clair : à quelle facture (quel paiement prévu) correspond-elle ? Réponse proposée, « rien à
// relier », ou choix dans la liste ; ou, décision du 19/10/2026, l'autre opération du relevé qui
// l'annule (débit remboursé, caution rendue) : les deux se compensent, aucune facture à chercher.
// Aussi dans la fiche de sa tâche « Rapprochement bancaire »
// (`dansTache`), sans changer de page. Une opération appariée au même débit lu chez le cabinet (décision du 13/10/2026) dit où
// se choisit sa pièce, dans les justificatifs.

/** Raisons de la règle de rapprochement, dites simplement. */
const RAISONS: Record<string, string> = {
  'montant exact': 'même montant',
  'montant proche (frais ou devise)': 'montant proche',
  'paiement partiel': 'règle une partie',
  date: 'date proche',
  IBAN: 'même compte bancaire',
  'libellé du tiers': 'même fournisseur',
  'libellé du paiement': 'même libellé',
  'fournisseur nommé': 'même fournisseur',
  'change sûr': 'taux de change plausible',
  'numéro de facture': 'même numéro de facture',
  'regroupement exact': 'la somme tombe juste',
  'achats du même tiers, somme exacte': 'achats du même fournisseur, la somme tombe juste',
}

function confiance(score: number) {
  if (score >= 70) return { texte: 'Très probable', ton: 'ton-ok' }
  if (score >= 40) return { texte: 'Possible', ton: 'ton-ambre' }
  return { texte: 'Peu probable', ton: '' }
}

const ETATS: Record<string, string> = {
  paye: 'noté payé, pas encore vu sur le relevé',
  partiel: 'payé en partie',
  programme: 'programmé',
}

/** Raisons sur le montant : la colonne du montant les dit déjà, elles ne sont pas répétées. */
const MONTANT_DIT = new Set([
  'montant exact',
  'montant proche (frais ou devise)',
  'paiement partiel',
  'regroupement exact',
  'achats du même tiers, somme exacte',
])

/** Le montant d'une proposition face à l'opération (colonne de droite de sa ligne). */
type Accord = {
  somme: string
  texte: string
  ton: 'ok' | 'ambre' | 'neutre'
  /** Paiement plus grand que l'opération : la part qu'elle en réglerait (en %) et le reste dû. */
  jauge?: { part: number; regle: string; reste: string }
}

/** Lignes montrées avant « Voir les autres ». */
const PREMIERS = 6

type Fait = { ecartee: boolean; texte: string }

/** L'écart en jours entre la date prévue et l'opération, dit simplement. */
function ecartDit(datePrevue: string, dateOperation: string): string | null {
  const e = ecartJours(datePrevue, dateOperation)
  if (Math.abs(e) <= 3) return null
  return e > 0 ? `${e} jours avant l’opération` : `${-e} jours après l’opération`
}

export function Rapprochement({
  id,
  dansTache = false,
  apres,
}: {
  id: string
  /** Dans la fiche de la tâche : sans en-tête de page ; une fois tranchée, la tâche suivante. */
  dansTache?: boolean
  apres?: () => void
}) {
  const client = useQueryClient()
  const navigate = useNavigate()
  const donnees = useQuery(requetes.propositions(id))
  const decisions = useQuery(requetes.decisions)
  const aRelier = useQuery(requetes.transactionsNonAffectees)
  const entites = useQuery(requetes.entites)
  const [libres, setLibres] = useState<Set<string>>(new Set())
  const [liste, setListe] = useState(false)
  const [compense, setCompense] = useState(false)
  const [tout, setTout] = useState(false)
  const [autreSens, setAutreSens] = useState(false)
  const [recherche, setRecherche] = useState('')
  const [fait, setFait] = useState<Fait | null>(null)
  const [enCours, setEnCours] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)

  // Opération suivante : les décisions d'abord, puis les opérations sans paiement relié ; celles
  // du foyer, cachées par défaut sur les relevés, seulement si l'on vient du foyer.
  const foyers = new Set((entites.data ?? []).filter((e) => e.type === 'foyer').map((e) => e.id))
  const depuisFoyer = !!donnees.data && foyers.has(donnees.data.transaction.entiteId)
  const suivante = [
    ...(decisions.data ?? []).map((d) => d.transactionId),
    ...(aRelier.data ?? []).filter((t) => depuisFoyer || !foyers.has(t.entiteId)).map((t) => t.id),
  ].find((x): x is string => !!x && x !== id)

  async function agir(action: () => Promise<unknown>, resultat: Fait | null) {
    setErreur(null)
    setEnCours(true)
    try {
      await action()
      await client.invalidateQueries()
      setFait(resultat)
      // Dans la fiche de la tâche : tranchée, elle se ferme ; on passe à la suivante de la file.
      if (dansTache && resultat) apres?.()
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'L’opération n’a pas pu être enregistrée.')
    } finally {
      setEnCours(false)
    }
  }

  const relier = (
    affectations: { paiementId: string; montantAffecteCts: number }[],
    quoi: string,
  ) =>
    agir(() => lire(api.rapprochements.$post({ json: { transactionId: id, affectations } })), {
      ecartee: false,
      texte: `Opération reliée à : ${quoi}.`,
    })
  const ecarter = () =>
    agir(() => lire(api.transactions[':id'].ignorer.$post({ param: { id } })), {
      ecartee: true,
      texte: 'Opération écartée : elle ne vous sera plus proposée.',
    })
  const reprendre = () =>
    agir(() => lire(api.transactions[':id'].reprendre.$post({ param: { id } })), null)
  const compenser = (avec: { id: string; dateOperation: string; libelle: string }) =>
    agir(
      () =>
        lire(api.transactions[':id'].compenser.$post({ param: { id }, json: { avecId: avec.id } })),
      {
        ecartee: false,
        texte: `Elle se compense avec l’opération du ${formaterJour(avec.dateOperation)} (${avec.libelle}) : rien à relier pour l’une ni pour l’autre.`,
      },
    )
  const decompenser = (compensationId: string) =>
    agir(
      () => lire(api.rapprochements[':id'].annuler.$post({ param: { id: compensationId } })),
      null,
    )

  if (donnees.isPending)
    return dansTache ? (
      <Vide>Chargement…</Vide>
    ) : (
      <main>
        <Vide>Chargement…</Vide>
      </main>
    )
  if (!donnees.data) {
    return dansTache ? (
      <p className="alerte">Opération introuvable.</p>
    ) : (
      <main>
        <p className="alerte">Opération introuvable.</p>
        <Link to="/releves">Retour aux relevés</Link>
      </main>
    )
  }
  const {
    transaction: t,
    propositions,
    candidats,
    operationCabinet,
    compensation,
    compensables,
  } = donnees.data
  const proposees = compensables.filter((c) => c.proposee)
  // Le sens de la ligne entière : une ligne compensée n'a plus de reste.
  const sortie = t.montantCts < 0
  const resteAbs = Math.abs(t.reste)
  const sensAttendu = sortie ? 'payer' : 'encaisser'
  const entite = entites.data?.find((e) => e.id === t.entiteId)?.nom
  const [annee, mois, jour] = t.dateOperation.split('-').map(Number) as [number, number, number]

  // Un paiement dans une autre devise (facture en dollars) se compare au bas de la bande de change
  // du débit, comme le serveur le couvre (`couvertDansLaDevise`) : jamais des dollars contre des
  // euros.
  const enDeviseOperation = (c: { resteCts: number; devise: string }) => {
    if (c.devise === t.devise) return c.resteCts
    const b = bornesDeChangeDebit(c.devise, t.devise)
    return b ? Math.ceil((c.resteCts * b.min) / 1000) : c.resteCts
  }
  const auChange = (c: { resteCts: number; devise: string }) =>
    c.devise !== t.devise && changeDansLaBandeDebit(resteAbs, c.resteCts, c.devise)

  // Liste : le même sens que l'opération, les montants les plus proches d'abord, puis les dates.
  const mot = recherche.trim().toLowerCase()
  const tries = candidats
    .filter((c) => autreSens || c.sens === sensAttendu)
    .filter(
      (c) =>
        !mot ||
        [c.libelle, c.tiers, c.numero].some((x) => x?.toLowerCase().includes(mot)) ||
        formaterMontant(c.resteCts).includes(mot),
    )
    .map((c) => ({
      ...c,
      auChange: auChange(c),
      ecart: auChange(c) ? 0 : Math.abs(enDeviseOperation(c) - resteAbs),
      jours: c.datePrevue ? Math.abs(ecartJours(c.datePrevue, t.dateOperation)) : 9999,
    }))
    .sort((a, b) => a.ecart - b.ecart || a.jours - b.jours)
  const coches = new Set([...libres].filter((x) => candidats.some((c) => c.id === x)))
  // Loin de l'opération sans montant concordant (`JOURS_MAX_SANS_CONCORDANCE`) : sous « Voir les autres ».
  const premiers = new Set(
    tries.filter((c) => c.ecart === 0 || c.jours <= JOURS_MAX_SANS_CONCORDANCE).slice(0, PREMIERS),
  )
  const visibles = tout || mot ? tries : tries.filter((c) => premiers.has(c) || coches.has(c.id))
  const caches = tries.length - visibles.length

  // Chaque paiement coché est couvert pour son reste dû, signé comme l'opération
  // (encaissement +, paiement -), en centimes de l'opération. Un seul paiement : toute l'opération
  // s'il est dans une autre devise (le serveur le solde au change), sinon au plus son reste. Le
  // serveur vérifie la somme.
  const choix = candidats.filter((c) => coches.has(c.id))
  const affectationsLibres = choix.map((c) => {
    const sens = c.sens === 'encaisser' ? 1 : -1
    const part =
      choix.length === 1
        ? c.devise === t.devise
          ? Math.min(c.resteCts, resteAbs)
          : resteAbs
        : enDeviseOperation(c)
    return { paiementId: c.id, montantAffecteCts: sens * part }
  })
  const somme = affectationsLibres.reduce((s, a) => s + a.montantAffecteCts, 0)
  const sommeCochee = choix.reduce((s, c) => s + enDeviseOperation(c), 0)
  const valable =
    choix.length > 0 && Math.sign(somme) === Math.sign(t.reste) && Math.abs(somme) <= resteAbs
  const seul = choix.length === 1 ? choix[0]! : null
  // Part du paiement seul que l'opération couvre, dans sa devise.
  const couvertSeul = seul ? couvertDansLaDevise(resteAbs, t.devise, seul.devise) : 0
  let bilan: { texte: string; alerte?: boolean } | null = null
  if (seul && couvertSeul < seul.resteCts)
    bilan = {
      texte: `Ce paiement ne sera réglé qu’en partie : il restera ${formaterMontant(seul.resteCts - couvertSeul, seul.devise)} à ${seul.sens === 'payer' ? 'payer' : 'encaisser'}.`,
    }
  else if (seul && seul.devise !== t.devise)
    bilan = {
      texte: `Le compte est bon : ${formaterMontant(resteAbs, t.devise)} soldent ${formaterMontant(seul.resteCts, seul.devise)} (change et frais compris).`,
    }
  else if (choix.length > 0 && !valable)
    bilan = {
      texte: `Trop : les paiements cochés font ${formaterMontant(sommeCochee, t.devise)}, plus que l’opération. Décochez-en un.`,
      alerte: true,
    }
  else if (choix.length > 0 && Math.abs(somme) < resteAbs)
    bilan = {
      texte: `Il restera ${formaterMontant(resteAbs - Math.abs(somme), t.devise)} de l’opération sans paiement relié.`,
    }
  else if (choix.length > 0) bilan = { texte: 'Le compte est bon.' }

  // Le montant d'une proposition comparé à l'opération, dit à droite de la ligne : même montant,
  // écart, ou, pour un paiement plus grand que l'opération, la jauge de ce qu'elle en réglerait.
  const leSens = sortie ? 'débit' : 'crédit'
  const accordDe = (affectations: { paiementId: string; montantAffecteCts: number }[]): Accord => {
    const total = affectations.reduce((s, a) => s + Math.abs(a.montantAffecteCts), 0)
    const seule = affectations.length === 1 ? affectations[0]! : null
    const paiement = seule ? candidats.find((x) => x.id === seule.paiementId) : undefined
    if (paiement && paiement.devise !== t.devise)
      return {
        somme: formaterMontant(paiement.resteCts, paiement.devise),
        texte: 'au change',
        ton: 'neutre',
      }
    if (paiement && total < paiement.resteCts)
      return {
        somme: formaterMontant(paiement.resteCts, paiement.devise),
        ton: 'ambre',
        texte: '',
        jauge: {
          part: Math.max(2, Math.round((total * 100) / paiement.resteCts)),
          regle: formaterMontant(total, t.devise),
          reste: formaterMontant(paiement.resteCts - total, paiement.devise),
        },
      }
    const somme = formaterMontant(total, t.devise)
    if (total === resteAbs)
      return { somme, texte: seule ? 'même montant' : 'la somme tombe juste', ton: 'ok' }
    return {
      somme,
      texte: `${leSens} plus élevé de ${formaterMontant(resteAbs - total, t.devise)}`,
      ton: 'neutre',
    }
  }

  const Conteneur = dansTache ? 'div' : 'main'
  return (
    <Conteneur className={dansTache ? 'rapprocher rapprocher-tache' : 'rapprocher'}>
      {!dansTache && (
        <header className="entete-fiche">
          <p className="fil">
            <Link to="/releves">Relevés</Link>
          </p>
          <h1>À quelle facture correspond cette opération ?</h1>
        </header>
      )}

      <section className="carte operation-a-relier">
        <div className="operation-tete">
          <span className={`pastille-sens operation-pastille ${sortie ? '' : 'entree'}`}>
            <Icone nom={sortie ? 'north_east' : 'south_west'} />
          </span>
          <div className="operation-somme">
            <span className="operation-etiquette">{sortie ? 'Débit' : 'Crédit'}</span>
            <span className={`operation-montant ${sortie ? '' : 'entree'}`}>
              {formaterMontant(t.montantCts, t.devise)}
            </span>
          </div>
          <time
            className="operation-date"
            dateTime={t.dateOperation}
            title={formaterJourLong(t.dateOperation)}
          >
            <span className="operation-date-mois">{MOIS[mois - 1]}</span>
            <span className="operation-date-jour">{jour}</span>
            <span className="operation-date-annee">{annee}</span>
          </time>
        </div>
        {t.reste !== t.montantCts && !compensation && (
          <p>
            <span className="badge grand ton-ambre">
              Déjà reliée en partie : reste {formaterMontant(resteAbs, t.devise)} à relier
            </span>
          </p>
        )}
        <dl className="operation-faits">
          <div>
            <dt className="operation-etiquette">Banque</dt>
            <dd className="titre-logo">
              <LogoConnu nom={t.banque} taille={22} />
              {t.banque}
            </dd>
          </div>
          <div>
            <dt className="operation-etiquette">Compte</dt>
            <dd>{t.compte}</dd>
          </div>
          {entite && (
            <div>
              <dt className="operation-etiquette">Entité</dt>
              <dd>{entite}</dd>
            </div>
          )}
        </dl>
        <div className="operation-libelle">
          <span className="operation-etiquette">Libellé sur le relevé</span>
          <p className="mono">{t.libelle}</p>
        </div>
        {/* Dans la tâche, « À faire ici » dit déjà ce qu'est le rapprochement. */}
        {(!dansTache || operationCabinet) && (
          <div className="operation-aide">
            {!dansTache && (
              <p>
                <Icone nom="help" />
                <span>
                  Rapprocher, c’est trouver à quelle facture correspond ce{' '}
                  {sortie ? 'débit' : 'crédit'}.
                </span>
              </p>
            )}
            {operationCabinet && (
              <p>
                <Icone nom="receipt_long" />
                <span>
                  {texteOperationCabinet(operationCabinet.portail)}{' '}
                  <Link to="/justificatifs/$id" params={{ id: operationCabinet.connexionId }}>
                    Ouvrir les justificatifs
                  </Link>
                </span>
              </p>
            )}
          </div>
        )}
      </section>

      {fait ? (
        <section className="carte operation-faite">
          <p className="operation-faite-titre">
            <Icone nom="check_circle" />
            <span>
              <strong>C’est noté.</strong> {fait.texte}
            </span>
          </p>
          <div className="boutons">
            {dansTache ? (
              apres && (
                <button type="button" className="bouton bouton-principal" onClick={apres}>
                  Tâche suivante
                  <Icone nom="arrow_forward" />
                </button>
              )
            ) : suivante ? (
              <button
                type="button"
                className="bouton bouton-principal"
                onClick={() => navigate({ to: '/rapprochements/$id', params: { id: suivante } })}
              >
                Opération suivante
                <Icone nom="arrow_forward" />
              </button>
            ) : (
              <span className="discret">Plus aucune opération à relier.</span>
            )}
            <Link to="/releves" className="bouton-discret">
              Retour aux relevés
            </Link>
            {fait.ecartee && (
              <button
                type="button"
                className="bouton-discret"
                disabled={enCours}
                onClick={reprendre}
              >
                Annuler
              </button>
            )}
          </div>
          {erreur && <p className="alerte">{erreur}</p>}
        </section>
      ) : compensation ? (
        <section className="carte operation-faite">
          <p className="operation-faite-titre">
            <Icone nom="sync_alt" />
            <span>
              {compensation.autre ? (
                <>
                  Elle se compense avec l’opération du{' '}
                  <strong>{formaterJour(compensation.autre.dateOperation)}</strong> (
                  {compensation.autre.libelle},{' '}
                  {formaterMontant(compensation.autre.montantCts, compensation.autre.devise)})
                </>
              ) : (
                'Elle se compense avec une autre opération'
              )}{' '}
              : {MOTIFS_COMPENSATION_DITS[compensation.motif]}. Rien à relier pour l’une ni pour
              l’autre.
            </span>
          </p>
          <div className="boutons">
            {compensation.autre && (
              <Link
                to="/rapprochements/$id"
                params={{ id: compensation.autre.id }}
                className="bouton-discret"
              >
                Voir l’autre opération
              </Link>
            )}
            <button
              type="button"
              className="bouton"
              disabled={enCours}
              onClick={() => decompenser(compensation.id)}
            >
              <Icone nom="link_off" />
              Annuler la compensation
            </button>
          </div>
          {erreur && <p className="alerte">{erreur}</p>}
        </section>
      ) : t.ignoreeLe ? (
        <section className="carte operation-faite">
          <p>Vous avez écarté cette opération : elle n’est plus proposée au rapprochement.</p>
          <div className="boutons">
            <button type="button" className="bouton" disabled={enCours} onClick={reprendre}>
              <Icone nom="restart_alt" />
              La remettre dans la liste
            </button>
          </div>
          {erreur && <p className="alerte">{erreur}</p>}
        </section>
      ) : (
        <>
          {proposees.length > 0 && (
            <section className="carte section">
              <div className="section-titre">
                <h2>Elle s’annule avec une autre opération</h2>
              </div>
              <p className="discret">
                Un {sortie ? 'crédit' : 'débit'} du même montant, chez le même fournisseur : les
                deux se compensent, aucune facture à chercher.
              </p>
              {proposees.map((c) => (
                <LigneCompensable
                  key={c.id}
                  c={c}
                  enCours={enCours}
                  onCompenser={() => compenser(c)}
                />
              ))}
            </section>
          )}
          {(propositions.length > 0 || proposees.length === 0) && (
            <section className="carte section">
              <div className="section-titre">
                <h2>
                  {propositions.length === 0
                    ? 'L’appli n’a rien trouvé'
                    : propositions.length === 1
                      ? 'L’appli pense que c’est ceci'
                      : 'L’appli hésite entre ces réponses'}
                </h2>
              </div>
              {propositions.length === 0 && (
                <p className="discret">
                  Aucun paiement prévu ne ressemble à cette opération. C’est souvent un achat par
                  carte, un abonnement ou des frais sans facture suivie : dans ce cas, choisissez «
                  Rien à relier » ci-dessous.
                </p>
              )}
              {propositions.map((p, i) => {
                const c = confiance(p.score)
                const quoi = p.affectations.map((a) => a.libelle ?? 'Paiement').join(', ')
                const accord = accordDe(p.affectations)
                const premiere = p.affectations[0]
                return (
                  <div key={i} className="ligne proposition">
                    <div className="ligne-contenu">
                      {p.affectations.length === 1 ? (
                        <strong className="proposition-titre">
                          {premiere?.libelle ?? 'Paiement'}
                        </strong>
                      ) : (
                        <ul className="proposition-groupe">
                          {p.affectations.map((a) => (
                            <li key={a.paiementId}>
                              <strong>{a.libelle ?? 'Paiement'}</strong>
                              <span className="proposition-part">
                                {formaterMontant(Math.abs(a.montantAffecteCts), t.devise)}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                      <span className="proposition-meta">
                        {[
                          premiere?.tiers,
                          premiere?.datePrevue
                            ? `prévu le ${formaterJour(premiere.datePrevue)}`
                            : null,
                          premiere?.datePrevue
                            ? ecartDit(premiere.datePrevue, t.dateOperation)
                            : null,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                      <span className="raisons">
                        <span className={`badge ${c.ton}`}>{c.texte}</span>
                        {p.raisons
                          .filter((r) => !MONTANT_DIT.has(r))
                          .map((r) => (
                            <span key={r} className="badge raison">
                              <Icone nom="check" />
                              {RAISONS[r] ?? r}
                            </span>
                          ))}
                      </span>
                    </div>
                    <div className={`proposition-montant accord-${accord.ton}`}>
                      <span className="proposition-somme">{accord.somme}</span>
                      {accord.jauge ? (
                        <>
                          <span
                            className="jauge"
                            role="img"
                            aria-label={`${accord.jauge.regle} réglés par ce ${leSens}, ${accord.jauge.reste} restants`}
                          >
                            <span style={{ width: `${accord.jauge.part}%` }} />
                          </span>
                          <span className="jauge-legende">
                            <span>
                              ce {leSens} <strong>{accord.jauge.regle}</strong>
                            </span>
                            <span>
                              reste <strong>{accord.jauge.reste}</strong>
                            </span>
                          </span>
                        </>
                      ) : (
                        <span className="proposition-accord">
                          {accord.ton === 'ok' && <Icone nom="check" />}
                          {accord.texte}
                        </span>
                      )}
                    </div>
                    <div className="ligne-actions">
                      <button
                        type="button"
                        className="bouton"
                        disabled={enCours}
                        onClick={() => relier(p.affectations, quoi)}
                      >
                        <Icone nom="check" />
                        Oui, c’est ça
                      </button>
                    </div>
                  </div>
                )
              })}
            </section>
          )}

          <section className="carte section">
            <div className="section-titre">
              <h2>
                {propositions.length === 0 && proposees.length === 0
                  ? 'Votre réponse'
                  : 'Ce n’est pas ça ?'}
              </h2>
            </div>
            <div className="choix-reponses">
              <button type="button" className="choix-reponse" disabled={enCours} onClick={ecarter}>
                <Icone nom="link_off" />
                <strong>
                  Rien à relier{' '}
                  {propositions.length === 0 && proposees.length === 0 && (
                    <span className="badge ton-ok">Conseillé</span>
                  )}
                </strong>
                <span className="discret">
                  Aucun paiement prévu dans l’appli : achat par carte, abonnement, frais bancaires,
                  virement entre vos comptes… L’opération sort de la liste ; vous pourrez annuler.
                </span>
              </button>
              <button
                type="button"
                className="choix-reponse"
                aria-pressed={liste}
                onClick={() => setListe((x) => !x)}
              >
                <Icone nom="search" />
                <strong>Je choisis le paiement</strong>
                <span className="discret">
                  Elle règle un paiement prévu dans l’appli : le chercher dans la liste.
                </span>
              </button>
              {compensables.length > proposees.length && (
                <button
                  type="button"
                  className="choix-reponse"
                  aria-pressed={compense}
                  onClick={() => setCompense((x) => !x)}
                >
                  <Icone nom="sync_alt" />
                  <strong>Elle s’annule avec une autre opération</strong>
                  <span className="discret">
                    Remboursée, rendue ou rejetée : choisir le {sortie ? 'crédit' : 'débit'} du même
                    montant.
                  </span>
                </button>
              )}
            </div>
            {erreur && !liste && <p className="alerte">{erreur}</p>}
          </section>

          {compense && (
            <section className="carte section">
              <div className="section-titre">
                <h2>Quelle opération l’annule ?</h2>
              </div>
              {compensables
                .filter((c) => !c.proposee)
                .map((c) => (
                  <LigneCompensable
                    key={c.id}
                    c={c}
                    enCours={enCours}
                    onCompenser={() => compenser(c)}
                  />
                ))}
            </section>
          )}

          {liste && (
            <section className="carte section">
              <div className="section-titre">
                <h2>Quel paiement prévu règle-t-elle ?</h2>
              </div>
              <p className="discret">
                Cochez le ou les paiements que ces {formaterMontant(resteAbs, t.devise)} règlent.
                Les montants les plus proches sont en haut.
              </p>
              <div className="champ recherche-paiements">
                <input
                  type="search"
                  aria-label="Chercher un paiement"
                  placeholder="Chercher un fournisseur, un libellé, un montant"
                  value={recherche}
                  onChange={(e) => setRecherche(e.target.value)}
                />
              </div>
              {tries.length === 0 && (
                <Vide>
                  {mot
                    ? 'Aucun paiement ne correspond à cette recherche.'
                    : `Aucun paiement ${sortie ? 'à payer' : 'à encaisser'} pour cette entité.`}
                </Vide>
              )}
              {visibles.map((c) => (
                <label key={c.id} className="ligne ligne-choix">
                  <input
                    type="checkbox"
                    checked={coches.has(c.id)}
                    onChange={(e) =>
                      setLibres((s) => {
                        const n = new Set(s)
                        if (e.target.checked) n.add(c.id)
                        else n.delete(c.id)
                        return n
                      })
                    }
                  />
                  <LogoConnu nom={c.tiers ?? c.libelle} />
                  <span className="ligne-contenu">
                    <span>{c.libelle ?? 'Paiement'}</span>
                    <span className="discret">
                      {[
                        c.tiers,
                        c.datePrevue ? `prévu le ${formaterJour(c.datePrevue)}` : null,
                        ETATS[c.statut] ?? (c.sens === 'payer' ? 'à payer' : 'à encaisser'),
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </span>
                  <span className="montant-choix">
                    <span className="montant">{formaterMontant(c.resteCts, c.devise)}</span>
                    {c.auChange ? (
                      <span className="badge ton-ok">même montant au change</span>
                    ) : c.ecart === 0 ? (
                      <span className="badge ton-ok">même montant</span>
                    ) : (
                      <span className="discret">écart {formaterMontant(c.ecart, t.devise)}</span>
                    )}
                  </span>
                </label>
              ))}
              <div className="boutons">
                {caches > 0 && (
                  <button type="button" className="bouton-discret" onClick={() => setTout(true)}>
                    Voir les {caches} autres paiements
                  </button>
                )}
                <label className="champ-case">
                  <input
                    type="checkbox"
                    checked={autreSens}
                    onChange={(e) => setAutreSens(e.target.checked)}
                  />
                  Montrer aussi les paiements {sortie ? 'à encaisser' : 'à payer'}
                </label>
              </div>
              <div className="bilan-choix">
                <p>
                  Opération : <span className="montant">{formaterMontant(resteAbs, t.devise)}</span>
                  {' · '}
                  Coché : <span className="montant">{formaterMontant(sommeCochee, t.devise)}</span>
                </p>
                {bilan ? (
                  <p className={bilan.alerte ? 'alerte' : 'discret'}>{bilan.texte}</p>
                ) : (
                  <p className="discret">Cochez au moins un paiement.</p>
                )}
                <div className="boutons">
                  <button
                    type="button"
                    className="bouton bouton-principal"
                    disabled={!valable || enCours}
                    onClick={() =>
                      relier(
                        affectationsLibres,
                        choix.map((c) => c.libelle ?? 'Paiement').join(', '),
                      )
                    }
                  >
                    <Icone nom="link" />
                    Relier {choix.length > 1 ? 'ces paiements' : 'ce paiement'} à l’opération
                  </button>
                </div>
                {erreur && <p className="alerte">{erreur}</p>}
              </div>
            </section>
          )}
        </>
      )}
    </Conteneur>
  )
}

/** Une opération du relevé qui pourrait compenser celle de l'écran. */
function LigneCompensable({
  c,
  enCours,
  onCompenser,
}: {
  c: {
    id: string
    compte: string
    dateOperation: string
    montantCts: number
    devise: string
    libelle: string
    ecartJours: number
    memeMarchand: boolean
    motif: keyof typeof MOTIFS_COMPENSATION_DITS
  }
  enCours: boolean
  onCompenser: () => void
}) {
  const jours = Math.abs(c.ecartJours)
  return (
    <div className="ligne proposition">
      <div className="ligne-contenu">
        <strong className="proposition-titre">{c.libelle}</strong>
        <span className="proposition-meta">
          {[
            c.compte,
            formaterJour(c.dateOperation),
            jours === 0
              ? 'le même jour'
              : `${jours} jour${jours > 1 ? 's' : ''} ${c.ecartJours > 0 ? 'après' : 'avant'}`,
          ].join(' · ')}
        </span>
        <span className="raisons">
          <span className="badge raison">
            <Icone nom="check" />
            même montant
          </span>
          {c.memeMarchand && (
            <span className="badge raison">
              <Icone nom="check" />
              même fournisseur
            </span>
          )}
          <span className="badge">{MOTIFS_COMPENSATION_DITS[c.motif]}</span>
        </span>
      </div>
      <div className="proposition-montant accord-ok">
        <span className="proposition-somme">{formaterMontant(c.montantCts, c.devise)}</span>
      </div>
      <div className="ligne-actions">
        <button type="button" className="bouton" disabled={enCours} onClick={onCompenser}>
          <Icone nom="sync_alt" />
          Oui, elles se compensent
        </button>
      </div>
    </div>
  )
}
